import React, { MouseEvent, PropsWithChildren, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import clsx from 'clsx';
import { ShippingLabelCarrier } from '@interfaces/models/timeline';
import Icon from '@components/atoms/icon/icon';
import CarrierName from '../carrier-name';
import styles from './carrier-collapse.module.scss';

interface ReassuranceDetailsProps extends PropsWithChildren {
  carrier: ShippingLabelCarrier;
  isSelected: boolean;
  hasOneOption?: boolean;
  handleSelectCarrier: (c: ShippingLabelCarrier) => void;
}

const LANG_PREFIX = 'MY_ACCOUNT.ORDERS_SALES.POPUP.GET_SHIPPING_LABEL.';

const CarrierCollapse = ({
  carrier,
  isSelected,
  hasOneOption,
  handleSelectCarrier,
  children,
}: ReassuranceDetailsProps) => {
  const { t } = useTranslation('orders-sales');

  const availableOptions = useMemo(() => {
    const AVAILABLE_OPTIONS_TRANSLATE_MAP = {
      shipping_code: t(`${LANG_PREFIX}LABEL_PREFERENCE.DIGITAL`),
      printable_label: t(`${LANG_PREFIX}LABEL_PREFERENCE.PRINTABLE`),
      home_collection: t(`${LANG_PREFIX}SHIPPING_OPTION.PICK_UP`),
      drop_off: t(`${LANG_PREFIX}SHIPPING_OPTION.DROP_OFF`),
    };

    return [...(carrier.shipmentLabelTypes || []), ...(carrier.dropOffTypes || [])].map((o) => ({
      type: o.type,
      label: AVAILABLE_OPTIONS_TRANSLATE_MAP[o.type],
      selected: o.selected,
    }));
  }, [carrier.dropOffTypes, carrier.shipmentLabelTypes, t]);

  const onClick = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    handleSelectCarrier(carrier);
  };

  return (
    <details
      open={isSelected}
      className={clsx(styles.collapse, {
        [styles.collapseClosed]: !isSelected,
      })}
    >
      <summary onClick={onClick}>
        <div className={styles.collapse_header}>
          <CarrierName
            carrier={carrier}
            isSelected={isSelected}
          />

          <div className={styles.collapse_arrow}>
            {isSelected && <p>{t('MY_ACCOUNT.ORDERS_SALES.POPUP.GET_SHIPPING_LABEL.SELECTED')}</p>}
            {!hasOneOption && (
              <Icon
                name="ios-arrow-right-default"
                className={clsx(styles.collapse_arrow_icon, {
                  [styles.collapse_arrow_icon_open]: isSelected,
                })}
              />
            )}
          </div>
        </div>

        {!isSelected && (
          <div className={styles.collapse_availableOptions}>
            {availableOptions.map((option) => (
              <div key={option.type}>{option.label}</div>
            ))}
          </div>
        )}
      </summary>

      <article>{children}</article>
    </details>
  );
};

export default CarrierCollapse;
